import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import PrinterIcon from "@redotech/redo-icons/printer.svg";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoButton,
  type RedoButtonSize,
} from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export const ParcelBoxLabelButton = memo(function ParcelBoxLabelButton({
  fulfillmentGroupId,
  parcelIndex,
  size = "sm",
}: {
  fulfillmentGroupId: string;
  parcelIndex: number;
  size?: RedoButtonSize;
}) {
  const [pending, setPending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const handleClick = useHandler(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setPending(true);
    try {
      const { url } = await rpcClient.getParcelBoxLabel({
        fulfillmentGroupId,
        parcelIndex,
      });
      setPreviewUrl(url);
    } catch {
      toast("Failed to generate box label", { variant: "error" });
    } finally {
      setPending(false);
    }
  });

  const handleDownload = useHandler(async () => {
    if (!previewUrl) {
      return;
    }
    const response = await fetch(previewUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `parcel-box-label-${parcelIndex + 1}.pdf`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  });

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={PrinterIcon}
        onClick={handleClick}
        pending={pending}
        size={size}
        text="Label"
      />
      {previewUrl && (
        <RedoModal
          isOpen={!!previewUrl}
          onModalCloseRequested={() => setPreviewUrl(null)}
          primaryButton={{ text: "Download", onClick: handleDownload }}
          secondaryButton={{
            text: "Close",
            onClick: () => setPreviewUrl(null),
          }}
          title="Box label preview"
        >
          <Flex height="half-screen" w="full">
            <iframe
              frameBorder="0"
              height="100%"
              src={previewUrl}
              title="Box label preview"
              width="100%"
            />
          </Flex>
        </RedoModal>
      )}
    </>
  );
});
